import React, { FC, useState } from "react";
import { Handle, NodeProps, Position, getOutgoers } from "reactflow";
import { handleDrawerAction, removeNode } from "reducers/flow-builder.reducer";
import ApiService from "services/api.service";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { v4 as uuid } from "uuid";
import { DrawerAction } from "../Drawer/drawer.fixtures";
import { NodeType } from "../FlowEditor";
import { NodeData } from "./NodeData";

export const EmptyNode: FC<NodeProps<NodeData>> = ({
  isConnectable,
  id,
  data: { disabled },
}) => {
  const drawerActionToNodeTypeMap: Record<DrawerAction, NodeType> = {
    [DrawerAction.IN_APP_MESSAGE]: NodeType.MESSAGE,
    [DrawerAction.TRACKER]: NodeType.MESSAGE,
    [DrawerAction.EMAIL]: NodeType.MESSAGE,
    [DrawerAction.INTERNAL_EMAIL]: NodeType.MESSAGE,
    [DrawerAction.EXIT]: NodeType.EXIT,
    [DrawerAction.JUMP_TO]: NodeType.JUMP_TO,
    [DrawerAction.PUSH]: NodeType.MESSAGE,
    [DrawerAction.SLACK]: NodeType.MESSAGE,
    [DrawerAction.SMS]: NodeType.MESSAGE,
    [DrawerAction.TIME_DELAY]: NodeType.TIME_DELAY,
    [DrawerAction.TIME_WINDOW]: NodeType.TIME_WINDOW,
    [DrawerAction.USER_ATTRIBUTE]: NodeType.USER_ATTRIBUTE,
    [DrawerAction.WAIT_UNTIL]: NodeType.WAIT_UNTIL,
    [DrawerAction.WEBHOOK]: NodeType.MESSAGE,
    [DrawerAction.MULTISPLIT]: NodeType.MULTISPLIT,
    [DrawerAction.EXPERIMENT]: NodeType.EXPERIMENT,
    [DrawerAction.RESPONSE_BRANCH]: NodeType.RESPONSE_BRANCH,
    [DrawerAction.AI_RESPONSE]: NodeType.AI_RESPONSE,
    [DrawerAction.RICOCHET]: NodeType.RICOCHET,
  };

  const { nodes, edges, flowId } = useAppSelector((state) => state.flowBuilder);
  const dispatch = useAppDispatch();

  const [isDraggedOver, setIsDraggedOver] = useState(false);

  const isTargetForStart = edges.some(
    (edge) =>
      edge.target === id &&
      nodes.find((node) => node.id === edge.source)?.type === NodeType.START
  );

  // The incoming branch edge already renders its own floating label
  // chip via BranchEdge for any branching parent (Response Branch,
  // Multisplit, Wait Until, Experiment). Duplicating the same label
  // inside the empty drop slot's body produced visible double-labeling
  // for Response Branch, so the slot now always shows the generic
  // "Next step" prompt and defers branch identification to the chip.

  const thisNode = nodes.find((node) => node.id === id);
  const outgoers = thisNode ? getOutgoers(thisNode, nodes, edges) : [];

  return (
    <div
      className={`empty-node w-[260px] h-[80px] rounded-lg bg-themed-background border-2 border-dashed border-[#9CA3AF] flex justify-center items-center ${
        disabled ? "opacity-50 cursor-not-allowed" : ""
      } ${isDraggedOver ? "flow-drop-highlight" : ""}${
        outgoers.length === 0 ? " last-empty-node" : ""
      }`}
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
      }}
      onDragEnter={() => setIsDraggedOver(true)}
      onDragLeave={() => setIsDraggedOver(false)}
      onDrop={(e) => {
        const action = e.dataTransfer.getData("action");

        // Optimistic UUID: generate the stepId client-side and swap the
        // node into its typed form immediately so the side panel opens
        // without waiting for the POST roundtrip. The server's
        // CreateStepDto accepts a client-supplied id and uses it verbatim
        // (steps.service.ts:insert).
        const stepId = uuid();

        dispatch(handleDrawerAction({ id, action, stepId }));
        setIsDraggedOver(false);

        (async () => {
          try {
            await ApiService.post({
              url: "/steps",
              options: {
                id: stepId,
                type: drawerActionToNodeTypeMap[action as DrawerAction],
                workflowID: flowId,
              },
            });
          } catch (err) {
            // Persist failed — roll back the optimistic node.
            dispatch(removeNode(id));
          }
        })();
      }}
    >
      <Handle
        position={Position.Top}
        type="target"
        isConnectable={isConnectable}
        className="!min-h-[1px] !h-[1px] !top-[1px] !opacity-0 !border-0 pointer-events-none cursor-default"
      />
      {!isDraggedOver && (
        <div className="text-center px-3">
          {isTargetForStart ? "Drag a component to start" : "Next step"}
        </div>
      )}
      <Handle
        position={Position.Bottom}
        type="source"
        isConnectable={isConnectable}
        className="!min-h-[1px] !h-[1px] !bottom-[1px] !opacity-0 !border-0 !pointer-events-none !cursor-default"
      />
    </div>
  );
};
